import React from 'react';
import { FaRedo, FaSignOutAlt } from 'react-icons/fa';
import { IoWarningOutline } from 'react-icons/io5';
import { TbPlugConnectedX } from 'react-icons/tb';
import { SessionErrorInfo } from '../types';
import { useFormatters } from '../context/LocalizationContext';
import { useTranslation } from '../localization/useAppTranslation';
import { ThemedSpinner } from './ThemedSpinner';
import '../styles/ServerErrorScreen.css';

interface ServerErrorScreenProps {
  error: SessionErrorInfo;
  onRetry: () => void;
  onLogout?: () => void;
  isRetrying: boolean;
}

/**
 * ServerErrorScreen Component
 * 
 * Displays a user-friendly error message when the chat server is unreachable
 * or experiencing issues. Provides options to retry or return to login.
 */
export const ServerErrorScreen: React.FC<ServerErrorScreenProps> = ({
  error,
  onRetry,
  onLogout,
  isRetrying
}) => {
  const { t } = useTranslation('auth');
  const { formatDateTime } = useFormatters();
  // Determine error icon and styling based on error type
  const isNetworkError = error.isNetworkError;
  const isHandoffError = error.isHandoffError === true;
  const errorTitle = isHandoffError
    ? t('errors.handoff.title')
    : isNetworkError
      ? t('errors.network.title')
      : t('errors.server.title');
  
  const ErrorIcon = isNetworkError && !isHandoffError ? TbPlugConnectedX : IoWarningOutline;

  return (
    <div className="server-error-screen">
      <div className="error-container">
        <div className="error-header">
          <div className="error-icon-large">
            <ErrorIcon />
          </div>
          <h1 className="error-title">{errorTitle}</h1>
          <p className="error-subtitle">
            {isHandoffError
              ? t('errors.handoff.description')
              : isNetworkError
                ? t('errors.network.description')
                : t('errors.server.description')}
          </p>
        </div>

        <div className="error-content">
          <div className="error-message">
            <p>{error.message}</p>
          </div>

          <div className="error-suggestions">
            <h3>{isHandoffError ? t('errors.handoff.suggestionHeading') : (
              isNetworkError ? t('errors.network.suggestionHeading') : t('errors.server.suggestionHeading')
            )}</h3>
            <ul>
              {isHandoffError && (
                <li>{t('errors.handoff.suggestion')}</li>
              )}
              {isNetworkError && (
                <>
                  <li>{t('errors.network.suggestions.connection')}</li>
                  <li>{t('errors.network.suggestions.retry')}</li>
                </>
              )}
                {!isNetworkError && !isHandoffError && (
                <>
                  <li>{t('errors.server.suggestions.wait')}</li>
                  <li>{t('errors.server.suggestions.refresh')}</li>
                  <li>{t('errors.server.suggestions.support')}</li>
                </>
              )}
            </ul>
          </div>

          <div className="error-actions">
            {!isHandoffError && (
              <button
                className={`action-button retry-button ${isRetrying ? 'loading' : ''}`}
                onClick={onRetry}
                disabled={isRetrying}
                title={t('actions.retryConnectionTitle')}
              >
                <span className="button-content">
                  {isRetrying ? (
                    <>
                      <ThemedSpinner
                        size="sm"
                        className="button-spinner"
                        ariaLabel={t('actions.retryingConnectionLabel')}
                      />
                      {t('actions.retryingConnection')}
                    </>
                  ) : (
                    <>
                      <FaRedo className="button-icon" />
                      {t('actions.retryConnection')}
                    </>
                  )}
                </span>
              </button>
            )}
            
            {onLogout && (
              <button
                className="action-button logout-button"
                onClick={onLogout}
                disabled={isRetrying}
                title={t('actions.returnToSafetyCenterTitle')}
              >
                <FaSignOutAlt className="button-icon" />
                {t('actions.returnToSafetyCenter')}
              </button>
            )}
          </div>
        </div>

        <div className="error-footer">
          <small>
            {t('lastError', { time: formatDateTime(new Date(error.timestamp)) })}
          </small>
        </div>
      </div>
    </div>
  );
};
